import * as types from '../mutation-types';
import * as jobsiteService from '../../services/JobsiteService';
import * as languageService from '../../services/LanguageService';
import * as paidSubscriptionService from '../../services/PaidSubscriptionService';
import * as userService from '../../services/UserService';
import router from '../../routing/router';
import { normalizeJson } from '../../../shared/helpers';
import { defineStore } from "pinia";
import { useRepo } from "pinia-orm";
import { useMainStore } from "../store";
import { useCommonStore } from "../../../shared/state/common";
import Company from '../../models/Company';
import CompanyUser from '../../models/CompanyUser';
import EmailSignature from '../../models/EmailSignature';
import Jobsite from '../../models/Jobsite';
import PaidSubscription from "../../models/PaidSubscription";
import Role from '../../models/Role';
import User from '../../models/User';
import * as piniaModels from '../database';

export const useAuthenticationStore = defineStore('authentication', {
    state: () => ({
        authenticated: false,
        currentUserId: null,
        currentCompanyId: null,
        companySwitches: 0,
        permissions: [],
        loggingOut: false,
        companyRepo: useRepo(Company),
        companyUserRepo: useRepo(CompanyUser),
        emailRepo: useRepo(EmailSignature),
        jobsiteRepo: useRepo(Jobsite),
        mainStore: useMainStore(),
        paidSubscriptionRepo: useRepo(PaidSubscription),
        roleRepo: useRepo(Role),
        userRepo: useRepo(User),
        commonStore: useCommonStore(),
    }),
    getters: {
        currentUser(state) {
            return state.currentUserId
                ? state.userRepo
                    .with('companies')
                    .with('companyUsers')
                    .find(state.currentUserId)
                : null;
        },
        defaultSignature(state) {
            return state.currentUserId && state.currentCompanyId
                ? state.emailRepo
                    .where('user_id', state.currentUserId)
                    .whereHas('associatedCompanies', q => q.where('id', state.currentCompanyId))
                    .first()
                : null;
        },
        currentCompany(state) {
            return state.currentCompanyId
                ? state.companyRepo.with('jobsite').find(state.currentCompanyId)
                : null;
        },
        contentLanguage(state) {
            if (!this.currentCompany) {
                return state.locale || 'nl';
            }

            const companyLanguages = this.currentCompany.jobsite.active_languages;
            const userLanguage = this.currentUser.language;

            return companyLanguages.includes(userLanguage) ? userLanguage : companyLanguages[0];
        }
    },
    actions: {
        login({ data, ssoLogin, redirectAfterLogin }) {
            if (ssoLogin) {
                return axios.post(route('app.sso-login'), {
                    email: data.email
                }).then(response => {
                    if (response.data.ssoActive) {
                        localStorage.setItem('saml2Uuid', response.data.uuid);
                        localStorage.setItem('saml2Provider', response.data.provider);
                        router.push({ name: 'saml2-login', params: { uuid: response.data.uuid } });
                        return true;
                    } else {
                        return false;
                    }
                });
            } else {
                return userService.login(data.email, data.password, data.remember)
                    .then((response) => {
                        if (
                            'x-requirement' in response.headers &&
                            response.headers['x-requirement'] === 'two_factor_authentication'
                        ) {
                            router.push({
                                name: 'two-factor-authentication',
                                query: { path: redirectAfterLogin },
                            });
                        } else {
                            let redirectTo;
                            if (!redirectAfterLogin || !redirectAfterLogin.startsWith('/documents')) {
                                switch (response.data.attributes.two_factor_state) {
                                    case 'required':
                                        redirectTo = 'require-two-factor';
                                        break;
                                    case 'suggested':
                                        redirectTo = 'suggest-two-factor';
                                        break;
                                    default:
                                        redirectTo = null;
                                }
                            }
                            this.setUser(response.data).then(() => {
                                if (redirectTo) {
                                    router.push({
                                        name: redirectTo,
                                        query: { path: redirectAfterLogin },
                                    });
                                } else {
                                    window.location.href = redirectAfterLogin || '/';
                                }
                            });
                        }
                    });
            }
        },
        register(data) {
            return userService.register({
                data: {
                    type: 'users',
                    attributes: data
                }
            });
        },
        async resetPassword({ email, password, password_confirmation, token, isNewUser }) {
            await userService.resetPassword(email, password, password_confirmation, token, isNewUser).then(() => {
                return userService.login(email, password, false)
                    .then((response) => {
                        if (
                            "x-requirement" in response.headers &&
                            response.headers["x-requirement"] === "two_factor_authentication"
                        ) {
                            router.push({ name: "two-factor-authentication" });
                        } else {
                            this.setUser(response.data)
                                .then(() => {
                                    router.push("/");
                                });
                        }
                    });
            });
        },
        setUser(user) {
            return new Promise((resolve) => {
                resolve(this.userRepo.save(normalizeJson(user)));
            })
                .then((newUser) => {
                    const companies = newUser.companies;
                    const jobsiteData = companies.find(company => company.id === newUser.default_company_id)?.jobsite;
                    if (jobsiteData) {
                        this.jobsiteRepo.save(jobsiteData);
                    }
                    this[types.LOGIN_SUCCESS](newUser.id);
                    return { newUser, companies };
                })
                .then(({ newUser, companies }) => {
                    document.removeEventListener('click', function() {
                        showConfetti();
                    });

                    if (newUser.confetti) {
                        document.addEventListener('click', function(e) {
                            const x = (e.clientX / window.innerWidth);
                            const y = (e.clientY / window.innerHeight);
                            showConfetti(x, y);
                        });
                    }

                    if (newUser.permissions) {
                        this.setPermissions(newUser.permissions);
                    }

                    languageService.interfaceLanguages().then((data) => {
                        this.mainStore.setInterfaceLanguages(data);
                        localStorage.setItem('interfaceLanguages', JSON.stringify(data));
                    });

                    this.mainStore.setLocale(newUser.language);

                    const companyIds = companies.map(company => company.id);
                    return this.setCompany(
                        companyIds.includes(newUser.default_company_id)
                            ? newUser.default_company_id
                            : companyIds[0]
                    );
                }).then(() => { document.title = 'Jobtoolz'; });
        },
        switchCurrentCompany(companyId) {
            const exceptionModels = ['User', 'Jobsite', 'Role', 'Company', 'CompanyUser', 'Feature'];
            const newCompany = this.companyRepo.with('jobsite').find(companyId);

            // remove all data from the store
            Object.entries(piniaModels.default).forEach(([key, value]) => {
                if (!exceptionModels.includes(key)) {
                    useRepo(value).flush();
                }
            });

            //delete all users, roles & company_users
            //unrelated to current user and newly switched company
            this.companyUserRepo
                .where((cu) => { return cu.company_id !== companyId && cu.user_id !== this.currentUserId })
                .delete();

            this.roleRepo
                .where((r) => { return r.companyId !== companyId })
                .delete();

            this.userRepo
                .where((u) => { return u.id !== this.currentUserId })
                .delete();

            if (newCompany && newCompany.active) {
                jobsiteService.show(companyId)
                    .then(result => {
                        return this.jobsiteRepo.save(normalizeJson(result.data));
                    });
            }

            userService.update(this.currentUserId, {
                data: { attributes: { preferred_company_id: companyId } },
            })
                .then(result => {
                    return this.userRepo.save(normalizeJson(result));
                });

            // Load in paid subscriptions when the company changes.
            paidSubscriptionService.get(companyId).then(result => {
                this.paidSubscriptionRepo.save(normalizeJson(result));
            });

            this.mainStore.clearState(true);

            return this.setCompany(companyId);
        },
        setCompany(companyId) {
            this[types.COMPANY_SWITCHED](companyId);
            window.bus.dispatch('switched-company', companyId);
        },
        async logout() {
            this.loggingOut = true;
            return await userService.logout();
        },
        [types.LOGIN_SUCCESS](userId) {
            this.authenticated = true;
            this.currentUserId = userId;
            this.commonStore.setCurrentUserId(userId);
        },
        [types.LOGIN_ERROR]() {
            this.authenticated = false;
        },
        logoutSuccess() {
            this.authenticated = false;
            this.currentUserId = null;
            this.currentCompanyId = null;
            this.commonStore.resetState();
            this.mainStore.clearState();
        },
        [types.COMPANY_SWITCHED](companyId) {
            this.currentCompanyId = companyId;
            this.commonStore.setCurrentCompanyId(companyId);
            this.companySwitches += 1;
        },
        setPermissions(permissions) {
            this.permissions = JSON.parse(JSON.stringify(permissions));
        }
    }
});
